/**
 * ELK Layout Engine for Workflow Visualization
 *
 * PERFORMANCE CHARACTERISTICS:
 * - 1-50 nodes: Optimal ELK layout with full features
 * - 51-75 nodes: ELK layout with performance monitoring
 * - 76-500 nodes: Linear layout for better performance
 * - 501-999 nodes: Optimized linear layout
 * - 1000+ nodes: Virtualized rendering (shows first 100 + placeholder)
 *
 * LAYOUT STRATEGIES:
 * 1. ELK Layout: Best visual results, hierarchical, automatic edge routing
 * 2. Linear Layout: Vertical flow, respects parent-child relationships
 * 3. Virtualized Layout: For massive flows, progressive loading
 */

import { Edge, Node } from '@xyflow/react'
import ELK from 'elkjs/lib/elk.bundled.js'
import type { ElkNode } from 'elkjs/lib/elk-api'
import { FlowElementsType, IEdgeType } from '../types/wizard-flow'

const defaultLayoutOptions = {
    'elk.direction': 'DOWN',
    'elk.layered.crossingMinimization.semiInteractive': 'true',
    'elk.spacing.nodeNode': '80',
    'elk.layered.spacing.nodeNodeBetweenLayers': '80',
}

const mapLayoutOptionsToNodeType: Record<string, Record<string, string>> = {
    action: {
        'elk.direction': 'DOWN',
        'elk.layered.crossingMinimization.semiInteractive': 'true',
        'elk.spacing.nodeNode': '80',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
    },
    trigger: {
        'elk.direction': 'DOWN',
        'elk.layered.crossingMinimization.semiInteractive': 'true',
        'elk.spacing.nodeNode': '80',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
    },
    placeholderNode: {
        'elk.direction': 'DOWN',
        'elk.layered.crossingMinimization.semiInteractive': 'true',
        'elk.spacing.nodeNode': '80',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
    },
    placeholderCondition: {
        'elk.direction': 'DOWN',
        'elk.layered.crossingMinimization.semiInteractive': 'true',
        'elk.spacing.nodeNode': '80',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
    },
    loop: {
        'elk.direction': 'DOWN',
        'elk.algorithm': 'layered',
        'elk.spacing.nodeNode': '80',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
        'elk.padding': '[top=40,left=30,bottom=40,right=30]',
        'elk.contentAlignment': 'H_CENTER',
        'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
        'elk.layered.nodePlacement.bk.fixedAlignment': 'CENTER',
        'elk.edgeRouting': 'ORTHOGONAL',
        'elk.layered.spacing.edgeNodeBetweenLayers': '30',
        'elk.layered.mergeEdges': 'true',
        'elk.layered.unnecessaryBendpoints': 'true',
        'elk.layered.cycleBreaking.strategy': 'INTERACTIVE',
        'elk.layered.layering.strategy': 'NETWORK_SIMPLEX',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
        'elk.layered.nodePlacement.favorStraightEdges': 'true',
        'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
    },
    condition_group: {
        'elk.direction': 'DOWN',
        'elk.algorithm': 'layered',
        'elk.spacing.nodeNode': '20',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
        'elk.padding': '[top=40,left=30,bottom=40,right=30]',
        'elk.contentAlignment': 'H_CENTER',
        'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
        'elk.layered.nodePlacement.bk.fixedAlignment': 'CENTER',
        'elk.edgeRouting': 'ORTHOGONAL',
        'elk.layered.spacing.edgeNodeBetweenLayers': '30',
        'elk.layered.mergeEdges': 'false',
        'elk.layered.unnecessaryBendpoints': 'true',
        'elk.layered.cycleBreaking.strategy': 'INTERACTIVE',
        'elk.layered.layering.strategy': 'NETWORK_SIMPLEX',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
        'elk.portAlignment.default': 'CENTER',
        'elk.layered.nodePlacement.favorStraightEdges': 'true',
    },
    condition: {
        'elk.direction': 'DOWN',
        'elk.algorithm': 'layered',
        'elk.hierarchyHandling': 'SEPARATE_CHILDREN',
        'elk.edgeRouting': 'ORTHOGONAL',
        'elk.spacing.nodeNode': '20',
        'elk.layered.spacing.nodeNodeBetweenLayers': '20',
        'elk.contentAlignment': 'H_CENTER',
        'elk.portAlignment.default': 'CENTER',
        'elk.portConstraints': 'FIXED_ORDER',
    },
}

const doElkLayout = async (nodes: Node[], edges: Edge[]): Promise<ElkNode> => {
    const layout: ElkNode = {
        id: 'root',
        layoutOptions: {
            // Prior ELK configuration (pre-virtualization change)
            'elk.algorithm': 'elk.layered',
            'elk.direction': 'DOWN',
            'elk.spacing.nodeNode': '50',
            'elk.layered.spacing.nodeNodeBetweenLayers': '80',
            'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
            'elk.edgeRouting': 'ORTHOGONAL',
            'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
            'elk.layered.nodePlacement.favorStraightEdges': 'true',
            'elk.contentAlignment': 'H_CENTER',
            'elk.nodeSize.constraints': 'FIXED_SIZE',
            'elk.portAlignment.default': 'CENTER',
            'elk.separateConnectedComponents': 'false',
            'elk.spacing.componentComponent': '0',
            'elk.layered.spacing.edgeNodeBetweenLayers': '30',
            'elk.layered.spacing.edgeEdgeBetweenLayers': '30',
            'elk.layered.mergeEdges': 'false',
            'elk.layered.unnecessaryBendpoints': 'true',
            'elk.layered.nodePlacement.bk.fixedAlignment': 'CENTER',
            'elk.layered.cycleBreaking.strategy': 'DEPTH_FIRST',
            'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
            'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
            'elk.spacing.individualOverride': '[source=condition,target=action,spacing=80]',
        },
        children: nodes
            .filter(node => !node.parentId)
            .map(node => {
                // Create completely clean object for ELK
                const cleanNode = {
                    id: String(node.id),
                    width: Number(node.width || 230),
                    height: Number(node.height || 60),
                    layoutOptions:
                        mapLayoutOptionsToNodeType[node.type || 'action'] ||
                        mapLayoutOptionsToNodeType['action'] ||
                        defaultLayoutOptions,
                    children: nodes
                        .filter(n => n.parentId === node.id)
                        .map(n => ({
                            id: String(n.id),
                            width: Number(n.width || 230),
                            height: Number(n.height || 60),
                        })),
                }
                return cleanNode
            }),
        edges: edges
            .filter(edge => edge.source && edge.target)
            .map(edge => ({
                id: String(edge.id),
                sources: [String(edge.source)],
                targets: [String(edge.target)],
            })),
    }

    try {
        const elk = new ELK()
        const elkLayout = await elk.layout(layout)
        return Promise.resolve(elkLayout)
    } catch (error) {
        return Promise.reject(error)
    }
}

// Order parent nodes for large linear layouts, prioritizing the main path from the trigger.
// Prefers an explicit data.next pointer if present; otherwise falls back to edge topology.
const topologicallyOrderParents = (parents: Node[], edges: Edge[]): Node[] => {
    const idToParent = new Map<string, Node>()
    parents.forEach(n => {
        if (n.id != null) idToParent.set(String(n.id), n)
    })

    // Build adjacency with preference to data.next if provided
    const adjacency = new Map<string, Set<string>>()
    const addAdj = (a: string, b: string) => {
        const set = adjacency.get(a) || new Set<string>()
        set.add(b)
        adjacency.set(a, set)
    }

    parents.forEach(n => {
        const nextId = (n as any)?.data?.next
        if (nextId && idToParent.has(String(nextId))) addAdj(String(n.id), String(nextId))
    })

    // Fall back to edges between parent nodes for any missing links
    edges.forEach(e => {
        if (e.source == null || e.target == null) return
        const s = String(e.source)
        const t = String(e.target)
        if (!idToParent.has(s) || !idToParent.has(t)) return
        addAdj(s, t)
    })

    // Find a starting trigger (or any zero‑inbound node) and do a BFS to get a stable main-path order
    const inbound = new Map<string, number>()
    Array.from(idToParent.keys()).forEach(id => inbound.set(id, 0))
    adjacency.forEach(targets => targets.forEach(t => inbound.set(t, (inbound.get(t) || 0) + 1)))

    const triggers = parents.filter(p => (p as any)?.type === 'trigger').map(p => String(p.id))
    const queue: string[] =
        triggers.length > 0 ? [...triggers] : Array.from(idToParent.keys()).filter(id => (inbound.get(id) || 0) === 0)

    const ordered: Node[] = []
    const seen = new Set<string>()
    while (queue.length) {
        const id = queue.shift() as string
        if (seen.has(id)) continue
        seen.add(id)
        const node = idToParent.get(id)
        if (node) ordered.push(node)
        const neigh = Array.from(adjacency.get(id) || [])
        neigh.forEach(nid => {
            if (!seen.has(nid)) queue.push(nid)
        })
    }

    // Append remaining in original order to keep stability for disconnected/cycles
    parents.forEach(n => {
        const id = String(n.id)
        if (!seen.has(id)) ordered.push(n)
    })

    return ordered
}

const createLinearFlowLayout = (nodes: Node[], edges: Edge[]): FlowElementsType => {
    // Linear flow layout for large flows - respects parent-child relationships
    const flowElements: FlowElementsType = { nodes: [], edges: [] }
    const VERTICAL_SPACING = 100
    const HORIZONTAL_SPACING = 350
    const CHILD_INDENT = 50
    const CONTAINER_PADDING = 40 // Padding inside container blocks

    // Separate parent and child nodes
    const parentNodes = nodes.filter(node => !node.parentId)
    const childNodes = nodes.filter(node => node.parentId)

    let currentY = 0

    // Layout parent nodes vertically with their children properly contained
    const orderedParents =
        nodes.length > PERFORMANCE_LIMITS.ELK_MAXIMUM ? topologicallyOrderParents(parentNodes, edges) : parentNodes
    orderedParents.forEach(parentNode => {
        const children = childNodes.filter(child => child.parentId === parentNode.id)
        const isContainer = parentNode.type === 'loop' || parentNode.type === 'condition_group'

        if (isContainer && children.length > 0) {
            // Container block with children
        }

        if (isContainer && children.length > 0) {
            // For container blocks (loops, condition groups), position children inside

            // Calculate the height needed for the container to hold all children
            const containerHeight = Math.max(280, children.length * VERTICAL_SPACING + CONTAINER_PADDING * 2)

            // Position the container block with proper sizing
            // @ts-ignore - Spreading existing node with new position
            flowElements.nodes.push({
                ...parentNode,
                position: { x: 0, y: currentY },
                height: containerHeight, // Size container to fit children
            })

            // Calculate container's internal starting position (relative to container)
            let childY = CONTAINER_PADDING

            // Position child nodes inside the container (positions relative to parent)
            children.forEach(childNode => {
                // @ts-ignore - Spreading existing node with new position
                flowElements.nodes.push({
                    ...childNode,
                    position: {
                        x: CONTAINER_PADDING, // Position relative to parent container
                        y: childY,
                    },
                    // Ensure child knows it's contained in the parent
                    parentId: parentNode.id,
                    extent: 'parent' as any,
                })
                childY += VERTICAL_SPACING
            })

            // Move past the container
            currentY += containerHeight + VERTICAL_SPACING / 2
        } else {
            // For regular nodes, use standard vertical layout

            // Position parent node
            // @ts-ignore - Spreading existing node with new position
            flowElements.nodes.push({
                ...parentNode,
                position: { x: 0, y: currentY },
            })

            currentY += VERTICAL_SPACING

            // Position any children next to the parent (non-container style)
            children.forEach(childNode => {
                // @ts-ignore - Spreading existing node with new position
                flowElements.nodes.push({
                    ...childNode,
                    position: {
                        x: CHILD_INDENT,
                        y: currentY,
                    },
                })
                currentY += VERTICAL_SPACING
            })

            // Add extra spacing between parent groups
            if (children.length > 0) {
                currentY += VERTICAL_SPACING / 2
            }
        }
    })

    // Add standalone child nodes (those without valid parent references)
    const orphanChildren = childNodes.filter(child => !parentNodes.some(parent => parent.id === child.parentId))
    orphanChildren.forEach((orphan, index) => {
        // @ts-ignore - Spreading existing node with new position
        flowElements.nodes.push({
            ...orphan,
            position: { x: HORIZONTAL_SPACING, y: index * VERTICAL_SPACING },
        })
    })

    // Add edges with proper styling
    edges.forEach(edge => {
        flowElements.edges.push({
            ...edge,
            style: { stroke: '#63316B', strokeWidth: 1, strokeOpacity: 1 },
            type: IEdgeType.CUSTOM_EDGE,
        })
    })

    return flowElements
}

// Performance limits and rendering strategies
export const PERFORMANCE_LIMITS = {
    ELK_OPTIMAL: 50, // Best visual layout with ELK
    ELK_MAXIMUM: 75, // Maximum for ELK before switching to linear
    LINEAR_MAXIMUM: 500, // Maximum for full rendering
    REQUIRES_VIRTUALIZATION: 1000, // Needs sophisticated rendering
} as const

const createVirtualizedLayout = (nodes: Node[], edges: Edge[]): FlowElementsType => {
    // For now, show first 100 nodes with a "load more" concept
    // In a full implementation, this would be viewport-based virtualization
    const VIEWPORT_LIMIT = 100
    const visibleNodes = nodes.slice(0, VIEWPORT_LIMIT)
    const relevantEdges = edges.filter(
        edge => visibleNodes.some(n => n.id === edge.source) && visibleNodes.some(n => n.id === edge.target)
    )

    const layout = createLinearFlowLayout(visibleNodes, relevantEdges)

    // Add a placeholder node to indicate there are more nodes
    if (nodes.length > VIEWPORT_LIMIT) {
        // @ts-ignore - Placeholder node with minimal structure
        layout.nodes.push({
            id: 'load-more-placeholder',
            type: 'placeholderNode',
            position: { x: 0, y: (VIEWPORT_LIMIT + 1) * 100 },
            data: {
                label: `+ ${nodes.length - VIEWPORT_LIMIT} more blocks...`,
                _id: 'load-more-placeholder',
                name: `+ ${nodes.length - VIEWPORT_LIMIT} more blocks...`,
                type: 'action' as any,
                isPlaceholder: true,
                totalNodes: nodes.length,
                visibleNodes: VIEWPORT_LIMIT,
            } as any,
            width: 300,
            height: 60,
        })
    }

    return layout
}

export const doLayout = async (nodes: Node[], edges: Edge[]): Promise<FlowElementsType> => {
    // Massive flows need virtualization, otherwise always use ELK
    if (nodes.length >= PERFORMANCE_LIMITS.REQUIRES_VIRTUALIZATION) {
        return Promise.resolve(createVirtualizedLayout(nodes, edges))
    }

    // Very large flows use linear layout
    if (nodes.length > PERFORMANCE_LIMITS.LINEAR_MAXIMUM) {
        return Promise.resolve(createLinearFlowLayout(nodes, edges))
    }

    // Large flows use linear layout
    if (nodes.length > PERFORMANCE_LIMITS.ELK_MAXIMUM) {
        return Promise.resolve(createLinearFlowLayout(nodes, edges))
    }

    // Store original node data to restore after layout
    const originalNodeData = new Map()
    const originalNodeType = new Map()
    nodes.forEach(node => {
        if (node.id) {
            originalNodeData.set(node.id, node.data)
            // @ts-ignore - Capture original node type for restoration after ELK
            originalNodeType.set(node.id, node.type)
        }
    })

    try {
        const elkLayout = await doElkLayout(nodes, edges)

        const flowElements: FlowElementsType = {
            nodes: [],
            edges: [],
        }

        elkLayout.children?.forEach(node => {
            if (node.id) {
                flowElements.nodes.push({
                    id: node.id,
                    // @ts-ignore
                    type: originalNodeType.get(node.id) || node.type || 'action',
                    position: { x: node.x || 0, y: node.y || 0 },
                    // @ts-ignore
                    data: originalNodeData.get(node.id) || node.data || { label: 'Node' },
                    // @ts-ignore
                    zIndex: node.zIndex || node.data?.zIndex || 0,
                    // @ts-ignore
                    selected: node.selected || false,
                    // @ts-ignore
                    style: node.style || {},
                    width: node.width || 230,
                    height: node.height || 60,
                })
            }

            if (node.children) {
                node.children.forEach(child => {
                    if (child.id) {
                        flowElements.nodes.push({
                            id: child.id,
                            // @ts-ignore
                            type: originalNodeType.get(child.id) || child.type || 'action',
                            position: { x: child.x || 0, y: child.y || 0 },
                            // @ts-ignore
                            data: originalNodeData.get(child.id) || child.data || { label: 'Node' },
                            parentId: node.id,
                            // @ts-ignore
                            zIndex: child.zIndex || child.data?.zIndex || 0,
                            // @ts-ignore
                            selected: child.selected || false,
                            // @ts-ignore
                            style: child.style || {},
                            width: child.width || 230,
                            height: child.height || 60,
                        })
                    }
                })
            }
        })

        elkLayout.edges?.forEach(edge => {
            if (edge.id && edge.sources && edge.sources[0] && edge.targets && edge.targets[0]) {
                flowElements.edges.push({
                    id: edge.id,
                    source: edge.sources[0],
                    target: edge.targets[0],
                    style: { stroke: '#63316B', strokeWidth: 1, strokeOpacity: 1 },
                    type: IEdgeType.CUSTOM_EDGE,
                })
            }
        })

        return Promise.resolve(flowElements)
    } catch (error) {
        return Promise.reject(error)
    }
}
